import { useEffect, useState } from 'react';
import { useApi } from './useApi';

export interface Me {
  objectId: string;
  displayName: string;
  roles: string[];
}

/**
 * Who the API thinks we are, including our roles, and whether we know yet.
 *
 * Asked of the server rather than read from the token in the browser, because
 * app roles are issued per resource: 'Admin' is in the access token audienced
 * to the API and is simply absent from the SPA's ID token. Reading
 * idTokenClaims.roles here looks right and always returns nothing.
 *
 * `loading` exists so a guard can tell "not an admin" apart from "do not know
 * yet". Without it an admin gets bounced off their own page in the moment
 * before the answer arrives.
 */
export function useMe(): { me: Me | null; loading: boolean } {
  const api = useApi();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    setLoading(true);
    api
      .get<Me>('/me')
      .then((result) => {
        if (!cancelled) setMe(result);
      })
      // Swallowed on purpose: this only decides whether an extra nav link is
      // drawn and whether a guard lets us through. Failing loudly would put an
      // error banner over a working app, and every route it guards is enforced
      // server-side regardless. Failing here means failing CLOSED.
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  return { me, loading };
}

export function useIsAdmin(): boolean {
  return useMe().me?.roles?.includes('Admin') ?? false;
}
